import React, { useMemo } from 'react';

import { AddAlt, StringInteger, StringText, SubtractAlt } from '@carbon/icons-react';
import { Field, FieldAttributes, FormikErrors, useField, useFormikContext } from 'formik';

import { BasicSelect } from 'components/BasicSelect/BasicSelects';
import IconButton from 'components/Buttons/IconButton/IconButton';
import TextButton from 'components/Buttons/TextButton/TextButton';

import FormDatePicker, { FormDatePickerProps } from 'app/components/FormFields/FormDatePicker/FormDatePicker';
import FormTextInputGroup, {
  FormTextInputGroupProps
} from 'app/components/FormFields/FormTextInputGroup/FormTextInputGroup';

import block from 'utils/bem-css-modules';
import { formatMessage } from 'utils/messages/utils';

import style from './AttributeFilterBuilder.module.pcss';
import { defaultOperator, defaultParametersByType, operatorsByType, ClauseValue } from './AttributeFilterUtils';
import { Attribute, AttributeType } from './useAccountAttributes';

const b = block(style);

export type AttributeFilterBuilderProps = {
  name: string;
  attributes: Attribute[];
  showErrors: boolean;
  portalId?: string;
  disabled?: boolean;
};

export const AttributeFilterBuilder = ({
  name,
  attributes,
  showErrors,
  portalId,
  disabled = false
}: AttributeFilterBuilderProps) => {
  const [clausesField] = useField<ClauseValue[]>(name);
  const { setFieldValue } = useFormikContext();

  const attributeItems = useMemo(
    () => attributes.map((attribute) => ({ text: attribute.name, value: attribute.column })),
    [attributes]
  );

  const attributeTypes = useMemo(
    () => new Map(attributes.map((attribute) => [attribute.column, attribute.type])),
    [attributes]
  );

  const setAttributeValue = (index: number, attribute: ClauseValue) => setFieldValue(`${name}.${index}`, attribute);

  const handleAddAttribute = () => {
    const usedColumns = new Set(clausesField.value.map((clause) => clause.column));
    const attributeToAdd = attributes.find((attribute) => !usedColumns.has(attribute.column)) ?? attributes[0];
    if (!attributeToAdd) return;
    setFieldValue(name, [
      ...clausesField.value,
      {
        column: attributeToAdd.column,
        operator: defaultOperator,
        parameters: defaultParametersByType[attributeToAdd.type]
      }
    ] satisfies React.SetStateAction<ClauseValue[]>);
  };

  const handleRemoveAttribute = (indexToRemove: number) => {
    setFieldValue(
      name,
      clausesField.value.filter((_, index) => index !== indexToRemove)
    );
  };

  return (
    <div>
      <ul className={b('clauseList')}>
        {clausesField.value.map((clause, index) => {
          const type = attributeTypes.get(clause.column);
          return (
            <li key={`${clause.column}-${index}`} className={b('clauseListItem')}>
              <div className={b('clauseInputGroup', { quad: clause.operator === 'inRange' })}>
                <BasicSelect
                  testId={`attribute-filter-builder-clause-column-${index}`}
                  label={formatMessage('COLUMN')}
                  items={attributeItems}
                  value={clause.column}
                  placement="bottom"
                  disabled={disabled}
                  onSelectedValueChange={(newColumn) => {
                    if (newColumn === clause.column) return;
                    const newType = attributeTypes.get(newColumn);
                    if (type === newType) {
                      setAttributeValue(index, {
                        column: newColumn,
                        operator: clause.operator,
                        parameters: clause.parameters
                      });
                    } else {
                      setAttributeValue(index, {
                        column: newColumn,
                        operator: defaultOperator,
                        parameters: defaultParametersByType[newType]
                      });
                    }
                  }}
                />
                <BasicSelect
                  testId={`attribute-filter-builder-clause-operator-${index}`}
                  label={formatMessage('OPERATOR')}
                  items={operatorsByType[type] ?? []}
                  value={clause.operator}
                  placement="bottom"
                  disabled={disabled}
                  onSelectedValueChange={(operator) => {
                    setAttributeValue(index, {
                      operator,
                      column: clause.column,
                      parameters: clause.parameters
                    });
                  }}
                />
                <AttributeFilterBuilderClause
                  name={name}
                  index={index}
                  type={type}
                  disabled={disabled}
                  operator={clause.operator}
                  portalId={portalId}
                />
              </div>
              <IconButton
                icon={<SubtractAlt />}
                minimal
                onClick={() => handleRemoveAttribute(index)}
                type="button"
                testId={`remove-attribute-${index}`}
                disabled={disabled}
              />
              {showErrors && <ClauseErrorMessage name={`${name}.${index}`} />}
            </li>
          );
        })}
      </ul>
      <div className={b('addAttributeContainer', { center: clausesField.value.length > 0 })}>
        <TextButton
          icon={<AddAlt />}
          minimal
          large={false}
          text={formatMessage('ADD_ATTRIBUTE')}
          type="button"
          testId="add-attribute"
          onClick={handleAddAttribute}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

const AttributeFilterBuilderClause = ({
  name,
  index,
  type,
  disabled,
  operator,
  portalId
}: {
  name: string;
  index: number;
  type: AttributeType;
  disabled: boolean;
  operator: string;
  portalId?: string;
}) => {
  let propsForType:
    | Omit<FieldAttributes<FormDatePickerProps>, 'name'>
    | Omit<FieldAttributes<FormTextInputGroupProps>, 'name'>;
  switch (type) {
    case 'date':
      propsForType = {
        component: FormDatePicker,
        popperPlacement: 'bottom',
        showCalendarIcon: true,
        fullWidth: true,
        placeholder: formatMessage('VALUE'),
        portalId
      };
      break;
    case 'number':
      propsForType = {
        component: FormTextInputGroup,
        type: 'number',
        placeHolder: formatMessage('VALUE'),
        leftIcon: <StringInteger />
      };
      break;
    case 'string':
      propsForType = {
        component: FormTextInputGroup,
        type: 'text',
        placeHolder: formatMessage('VALUE'),
        leftIcon: <StringText />
      };
      break;
    default:
      console.warn('Unexpected type', type);
      return null;
  }
  const genericProps = {
    showErrors: false,
    disabled
  };
  const namePrefix = `${name}.${index}.parameters`;
  const hasSecondField = operator === 'inRange';
  return (
    <>
      <Field
        {...genericProps}
        {...propsForType}
        name={type === 'date' ? `${namePrefix}.dateFrom` : `${namePrefix}.filter`}
        className={b('parametersInput', { isFinal: !hasSecondField })}
      />
      {hasSecondField && (
        <Field
          {...genericProps}
          {...propsForType}
          name={type === 'date' ? `${namePrefix}.dateTo` : `${namePrefix}.filterTo`}
          className={b('parametersInput', { isFinal: true })}
        />
      )}
    </>
  );
};

// We override the types here since Formik's built in type doesn't handle object types
const useClauseFieldError = useField as unknown as (
  name: string
) => [void, { error: string | FormikErrors<ClauseValue> }, void];

const ClauseErrorMessage = ({ name }: { name: string }) => {
  const [, { error }] = useClauseFieldError(name);

  const message = useMemo(() => {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (error.column) return error.column;
    if (error.operator) return error.operator;
    if (!error.parameters) return null;
    for (const key of parameterKeys) {
      if (error.parameters[key]) return error.parameters[key];
    }
    return null;
  }, [error]);

  if (!message) return null;
  return <div className={b('errorMessage')}>{message}</div>;
};

const parameterKeys = ['filter', 'filterTo', 'dateFrom', 'dateTo'] as const;
